import React, { useMemo } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { es } from "date-fns/esm/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CalendarPanelProps } from "../../../interfaces";

/** Monday-first, matching the calendar already used in the report filters. */
const WEEK_STARTS_ON = 1 as const;

const WEEK_DAY_LABELS: readonly string[] = ["L", "M", "M", "J", "V", "S", "D"];

/**
 * Month grid for the picker. It renders its own days instead of leaning on a
 * calendar library so every day cell can carry the dashboard's own tokens and
 * look the same in light and dark mode.
 */
export const CalendarPanel: React.FC<CalendarPanelProps> = (props) => {
  const { selectedDate, onSelectDate, visibleMonth, onVisibleMonthChange } =
    props;

  // A month grid always spans whole weeks, so the interval is padded out to the
  // surrounding Mondays and Sundays and the overflow days are dimmed below.
  const days: Date[] = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(visibleMonth), {
          weekStartsOn: WEEK_STARTS_ON,
        }),
        end: endOfWeek(endOfMonth(visibleMonth), {
          weekStartsOn: WEEK_STARTS_ON,
        }),
      }),
    [visibleMonth],
  );

  const monthLabel: string = format(visibleMonth, "MMMM yyyy", { locale: es });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Mes anterior"
          className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-surface-sunken hover:text-ink"
          onClick={() => onVisibleMonthChange(subMonths(visibleMonth, 1))}
        >
          <ChevronLeft className="h-4 w-4 shrink-0" />
        </button>
        <span className="text-sm font-semibold capitalize text-ink-strong">
          {monthLabel}
        </span>
        <button
          type="button"
          aria-label="Mes siguiente"
          className="flex h-7 w-7 items-center justify-center rounded-md text-ink-muted hover:bg-surface-sunken hover:text-ink"
          onClick={() => onVisibleMonthChange(addMonths(visibleMonth, 1))}
        >
          <ChevronRight className="h-4 w-4 shrink-0" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEK_DAY_LABELS.map((weekDay: string, index: number) => (
          <span
            key={`weekday-${index}`}
            className="flex h-7 items-center justify-center text-xs font-medium text-ink-muted"
          >
            {weekDay}
          </span>
        ))}

        {days.map((day: Date) => {
          const isSelected: boolean =
            selectedDate !== null && isSameDay(day, selectedDate);
          const isOutsideMonth: boolean = !isSameMonth(day, visibleMonth);

          return (
            <button
              key={day.toISOString()}
              type="button"
              aria-pressed={isSelected}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors ${
                isSelected
                  ? "bg-primary font-semibold text-white"
                  : "hover:bg-surface-sunken"
              }
              ${!isSelected && isOutsideMonth ? "text-ink-muted/60" : ""}
              ${!isSelected && !isOutsideMonth ? "text-ink" : ""}
              ${
                !isSelected && isToday(day)
                  ? "font-semibold text-primary ring-1 ring-primary/40"
                  : ""
              }`}
              onClick={() => onSelectDate(day)}
            >
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
};
